<template>
    <div id="fiche-bu-top-and-flop-stores-block" class="background-quaternary-lighter">
        <div class="flex text-center background-quaternary">
            <div class="col-12">
                <as-title class="color-white" :title="$t('ficheBu.detail.top_and_flop_stores_block.title')"
                    :size="4"></as-title>
            </div>
        </div>
        <div class="flex">
            <template v-if="!canEdit">
                <div id="top-stores" class="col-6 flex pt-0 pb-0 relative">
                    <div class="flex align-items-center w-100">
                        <div class="thumb-img mt-2">
                            <img :src="thumbsUpImage" />
                        </div>
                        <div class="flex-1 flex text-center">
                            <span id="top-stores__number" class="w-100">
                                <ProgressBar v-if="isBusy" mode="indeterminate" style="height: 6px; width: 40px;"
                                    class="mb-4 mt-3">
                                </ProgressBar>
                                <template v-else>
                                    {{ storesWithoutAccidentLast12Months }}
                                </template>
                            </span>
                            <span class="mb-auto p-1 w-100">
                                {{ $t("ficheBu.detail.top_and_flop_stores_block.without_accident_label") }}
                            </span>
                        </div>
                    </div>
                </div>
            </template>
            <template v-if="!canEdit">
                <div id="flop-stores" class="col-6 flex pt-0 pb-0 relative background-quaternary-light">
                    <div class="flex align-items-center w-100">
                        <div class="flex-1">
                            <as-title class="w-100 text-center mt-3"
                                :title="$t('ficheBu.detail.top_and_flop_stores_block.flop_title')" :size="6"></as-title>
                            <div v-if="!topAndFlopStoreValue" class="text-center mt-3 mb-3">
                                {{ $t('ficheBu.detail.top_and_flop_stores_block.no_store_label') }}
                            </div>
                            <div v-else class="tinymce-content " v-html="topAndFlopStoreValue">
                            </div>
                        </div>
                        <div class="thumb-img mt-2 ml-auto">
                            <img :src="thumbsDownImage" />
                        </div>
                    </div>
                </div>
            </template>
            <div v-if="canEdit" class="flex relative col-12">
                <div class="flex flex-column text-left w-100">
                    <div class="relative w-100">
                        <div id="translate-button" class="mt-3 mb-3" style="position: absolute; z-index: 1; right: 0;">
                            <TranslateButton :stringsToTranslate="getStringsToTranslate()"
                                @onTranslationSuccess="handleTranslationSuccess" />
                        </div>
                        <TabView>
                            <TabPanel>
                                <template #header>
                                    <div class="flex align-items-center gap-2">
                                        <Avatar :image="getFlag('fr')" shape="square" />
                                        <span class="font-bold white-space-nowrap">FR</span>
                                    </div>
                                </template>
                                <div class="mt-3">
                                    <TinyMceEditor v-model="topAndFlopStoreListFr"
                                        @update:modelValue="$forceUpdate()" />
                                </div>
                            </TabPanel>
                            <TabPanel>
                                <template #header>
                                    <div class="flex align-items-center gap-2">
                                        <div class="flex align-items-center gap-2">
                                            <Avatar :image="getFlag('en')" shape="square" />
                                            <span class="font-bold white-space-nowrap">EN</span>
                                        </div>
                                    </div>
                                </template>
                                <div class="mt-3">
                                    <TinyMceEditor v-model="topAndFlopStoreListEn"
                                        @update:modelValue="$forceUpdate()" />
                                </div>
                            </TabPanel>
                        </TabView>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { bufferToJpegBase64 } from '../../util';
import AsTitle from '@/components/Elements/AsTitle.vue';
import AsIcon from "@/components/Elements/AsIcon.vue";
import TinyMceEditor from "@/components/Editor/TinyMceEditor.vue";
import TranslateButton from "@/components/Elements/TranslateButton.vue";

import Avatar from "primevue/avatar";
import TabView from 'primevue/tabview';
import TabPanel from 'primevue/tabpanel';
import Textarea from 'primevue/textarea';
import InputNumber from 'primevue/inputnumber';
import ProgressBar from 'primevue/progressbar';

import roleMixins from "@/mixins/roleMixins";

export default {
    name: "FicheBuTopAndFlopStoresBlock",
    components: {
        AsTitle,
        AsIcon,
        Avatar,
        TabView,
        TabPanel,
        InputNumber,
        Textarea,
        ProgressBar,
        TinyMceEditor,
        TranslateButton
    },
    props: {
        edit: Boolean,
        isBusy: Boolean,
        ficheBu: Object,
        selectedEntreprise: Object,
        storesWithoutAccidentLast12Months: Number,
    },
    mixins: [roleMixins],
    computed: {
        canEdit() {
            return this.edit && this.isAdmin;
        },
        locale() {
            const locale = this.$i18n.locale
            const firstLetter = locale.charAt(0).toUpperCase()
            const rest = locale.slice(1).toLowerCase()
            return firstLetter + rest;
        },
        topAndFlopStoreListFr: {
            get() { return this.ficheBu?.topAndFlopStoreListFr || "" },
            set(val) { if (this.ficheBu) { this.ficheBu.topAndFlopStoreListFr = val; } }
        },
        topAndFlopStoreListEn: {
            get() { return this.ficheBu?.topAndFlopStoreListEn || "" },
            set(val) { if (this.ficheBu) { this.ficheBu.topAndFlopStoreListEn = val; } }
        },
        topAndFlopStoreValue() {
            return this.ficheBu?.['topAndFlopStoreList' + this.locale] || null
        },
        topAndFlopPercentValue: {
            get() { return this.ficheBu?.topAndFlopPercent || "" },
            set(val) { if (this.ficheBu) { this.ficheBu.topAndFlopPercent = val; } }
        },
    },
    methods: {
        getSelectedEntrepriseImage() {
            if (
                this.selectedEntreprise &&
                this.selectedEntreprise.photo &&
                this.selectedEntreprise.photo.data.length > 0
            ) {
                return bufferToJpegBase64(this.selectedEntreprise.photo.data);
            } else {
                return this.noUserImg;
            }
        },
        getFlag(iso) {
            return new URL(`../../assets/images/flags/${iso}.svg`, import.meta.url).href;
        },
        getStringsToTranslate() {
            const locale = this.$i18n.locale;
            return locale === 'fr' ? [this.topAndFlopStoreListFr] : [this.topAndFlopStoreListEn];
        },
        handleTranslationSuccess(translatedData, targetLangCode) {
            if (Array.isArray(translatedData) && translatedData.length === 1) {
                switch (targetLangCode) {
                    case 'en':
                        this.topAndFlopStoreListEn = translatedData[0];
                        break;
                    case 'fr':
                        this.topAndFlopStoreListFr = translatedData[0];
                        break;
                }
            }
        },
    },
    data() {
        return {
            thumbsUpImage: new URL("../../assets/images/icons/thumbs-up.png", import.meta.url).href,
            thumbsDownImage: new URL("../../assets/images/icons/thumbs-down.png", import.meta.url).href,
        };
    },
};
</script>

<style lang="scss">
@import "../../assets/styles/colors.scss";

#fiche-bu-top-and-flop-stores-block {
    #top-stores {
        &__number {
            font-size: 3em;
            font-weight: bold;
            line-height: normal;
        }
    }
}
</style>
